import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
// mantine
import { Pagination, Select, Table, TextInput } from '@mantine/core';
import { useDebouncedState } from '@mantine/hooks';
// components
import { TPersona } from '@newo/api/gateway';
import Header from '@/components/Layout/components/Header';
import PersonaRow from '@/pages/Personas/components/PersonaRow';
import PersonaDrawer from '@/pages/Personas/components/PersonaDrawer';
// utils
import useAKBStore from '@/pages/AKB/AKB.store.ts';
import { usePersonasQuery } from '@/services/bff/bff.queries.ts';
import { PageContainer } from '@/components/PageContainer/PageContainer.tsx';

type TParams = {
    query: string;
    page: number;
    link: string;
};

const DEBOUNCE_TIMEOUT = 200;

const Personas = () => {
    const navigate = useNavigate();
    const setAKBPersona = useAKBStore((state) => state.setPersona);
    const [params, setParams] = useDebouncedState<TParams>(
        {
            query: '',
            page: 1,
            link: ''
        },
        DEBOUNCE_TIMEOUT
    );

    const [selectedPersona, setSelectedPersona] = useState<
        TPersona | undefined
    >();
    const { data, refetch } = usePersonasQuery({
        per: 30,
        is_linked_to_agent: params.link
            ? params.link === 'with_agent'
            : undefined,
        page: params.page,
        query: params.query || undefined
    });

    const handleParamsChange = (newParams: Partial<TParams>) => {
        setParams((oldParams) => {
            return {
                ...oldParams,
                ...newParams
            };
        });
    };

    const handleLinkChange = (link: string | null) => {
        if (typeof link === 'string') {
            handleParamsChange({ link, page: 1 });
        }
    };

    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        handleParamsChange({
            query: event.currentTarget.value,
            page: 1
        });
    };

    const handleGoToAKB = (persona: TPersona) => {
        setAKBPersona(persona);
        navigate('/akb');
    };

    const handleDelete = () => {
        refetch();
    };

    return (
        <>
            <Helmet>
                <title>NEWO - Personas</title>
            </Helmet>
            <Header breadcrumbs={[{ label: 'Personas' }]} />
            <PageContainer pb={80} pt={20}>
                <div className="mb-4 flex gap-4">
                    <Select
                        data={[
                            {
                                value: '',
                                label: 'All'
                            },
                            {
                                value: 'with_agent',
                                label: 'With Agent'
                            },
                            {
                                value: 'without_agent',
                                label: 'Without Agent'
                            }
                        ]}
                        label="Type"
                        value={params.link}
                        onChange={handleLinkChange}
                    />
                    <TextInput
                        className="w-80"
                        defaultValue={params.query}
                        label="Search"
                        onChange={handleSearchChange}
                    />
                </div>
                <Table highlightOnHover>
                    <Table.Thead>
                        <Table.Tr>
                            <Table.Th>Name</Table.Th>
                            <Table.Th className="w-1/2">Description</Table.Th>
                            <Table.Th>Links</Table.Th>
                            <Table.Th className="w-60">Agent</Table.Th>
                            <Table.Th />
                        </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                        {data?.items.map((persona) => (
                            <PersonaRow
                                key={persona.id}
                                persona={persona}
                                onDelete={handleDelete}
                                onEdit={() => {
                                    setSelectedPersona(persona);
                                }}
                                onGoToAKB={handleGoToAKB}
                            />
                        ))}
                    </Table.Tbody>
                </Table>
                <div className="flex justify-center pb-6">
                    <Pagination
                        mt="sm"
                        total={
                            data?.metadata
                                ? Math.ceil(
                                      data.metadata.total / data.metadata.per
                                  )
                                : 1
                        }
                        value={params.page}
                        onChange={(page) => {
                            handleParamsChange({ page });
                        }}
                    />
                </div>
            </PageContainer>
            <PersonaDrawer
                persona={selectedPersona}
                refetch={() => {
                    refetch();
                }}
                onClose={() => {
                    setSelectedPersona(undefined);
                }}
            />
        </>
    );
};

export default Personas;
